import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as moment from 'moment';

import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import * as researcherActions from './um-researcher.actions';
import * as umStudyActions from '../um-study/um-study.actions';
import { SETTINGS_ROUTES } from '../../settings/settings.routes';
import { UmResearcher } from 'src/app/models/um-researcher';
import { ResearcherService } from '../../services/researcher/researcher.service';
import { DASHBOARD_ROUTES } from '../../dashboard.routes';
import { selectUmResearcherById } from './um-researcher.selector';
import { AppState } from '../../../state';
import { StudyType } from 'src/app/models/study-type';

@Injectable()
export class UmResearcherEffects {
  researchersRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter(
        (route: CustomRouterNavigatedAction) =>
          route.payload.routerState.url.includes(DASHBOARD_ROUTES.SETTINGS) &&
          route.payload.routerState.url.includes(SETTINGS_ROUTES.RESEARCHERS) &&
          !route.payload.routerState.params.hasOwnProperty('id')
      ),
      map(() => researcherActions.getUmResearchers())
    )
  );

  singleResearcherRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return (
          route.payload.routerState.url.includes(DASHBOARD_ROUTES.SETTINGS) &&
          route.payload.routerState.url.includes(SETTINGS_ROUTES.RESEARCHERS) &&
          route.payload.routerState.path.includes(`:id`)
        );
      }),
      map((action) =>
        researcherActions.selectUmResearcher({
          researcherId: action.payload.routerState.params.id,
        })
      )
    )
  );

  getResearchers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.getUmResearchers),
      switchMap(() => {
        return this.umResearchersService.getResearchers().pipe(
          map((researchers) => {
            const transformedResearchers = researchers.map((researcher) => {
              const transformedResearcher: UmResearcher = this.dashboardCamelCasePipe.keysToCamelCase(researcher);
              if (transformedResearcher.lastLogin) {
                transformedResearcher.lastLogin = moment(transformedResearcher.lastLogin).format('DD/MM/YYYY');
              }
              if (transformedResearcher.account) {
                transformedResearcher.account = transformedResearcher.account.toLowerCase();
              }
              if (transformedResearcher.active) {
                transformedResearcher.active = !!transformedResearcher.active;
              }
              if (transformedResearcher.type) {
                transformedResearcher.type = transformedResearcher.type.toString().split(',');
              }
              return transformedResearcher;
            });
            return researcherActions.getUmResearchersSuccess({
              researchers: transformedResearchers,
            });
          }),
          catchError(() => of(researcherActions.getUmResearchersError()))
        );
      })
    )
  );

  getResearchersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.getUmResearchersError),
        tap(() => this.toastrService.error('Error in fetching Researchers'))
      ),
    { dispatch: false }
  );

  selectUmResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.selectUmResearcher),
      switchMap(({ researcherId }) => {
        return this.store.pipe(
          select(selectUmResearcherById, { researcherId }),
          map((selectedUmResearcher) =>
            researcherActions.selectUmResearcherSuccess({
              selectedUmResearcher,
            })
          ),
          catchError(() => of(researcherActions.selectUmResearcherError()))
        );
      })
    )
  );

  selectUmResearcherSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.selectUmResearcherSuccess),
      map(({ selectedUmResearcher }) => {
        if (!selectedUmResearcher) {
          this.router.navigate([`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.RESEARCHERS}`]);
          return researcherActions.noopAction();
        }
        return umStudyActions.getResearcherStudies({
          researcherId: selectedUmResearcher?.id,
        });
      })
    )
  );

  activateResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.activateUmResearcher),
      switchMap(({ researcher }) => {
        return this.umResearchersService.activateResearcher(researcher.id).pipe(
          map(() =>
            researcherActions.activateUmResearcherSuccess({
              activatedResearcher: {
                ...researcher,
                active: !researcher.active,
              },
            })
          ),
          catchError((error) => of(researcherActions.activateUmResearcherError(error)))
        );
      })
    )
  );

  activateResearchersSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.activateUmResearcherSuccess),
        tap(() => this.toastrService.success('Researcher successfully activated'))
      ),
    { dispatch: false }
  );

  activateResearchersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.activateUmResearcherError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to activate Researcher'
          )
        )
      ),
    { dispatch: false }
  );

  blockResearcher$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.blockUmResearcher),
      switchMap(({ researcher }) => {
        return this.umResearchersService.blockResearcher(researcher.id).pipe(
          map(() =>
            researcherActions.blockUmResearcherSuccess({
              blockedResearcher: { ...researcher, active: !researcher.active },
            })
          ),
          catchError((error) => of(researcherActions.blockUmResearcherError(error)))
        );
      })
    )
  );

  blockResearchersSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.blockUmResearcherSuccess),
        tap(() => this.toastrService.success('Researcher successfully deactivated'))
      ),
    { dispatch: false }
  );

  blockResearchersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.blockUmResearcherError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to block Researcher'
          )
        )
      ),
    { dispatch: false }
  );

  changeUmResearcherRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.changeUmResearcherRole),
      switchMap(({ researcher, role }) => {
        return this.umResearchersService.changeResearcherAccountType(researcher.id, role).pipe(
          map((upgradedResearcher: UmResearcher) => {
            if (upgradedResearcher.superuser) {
              upgradedResearcher.type = [StudyType.CC, StudyType.TIIM];
            }
            return researcherActions.changeUmResearcherRoleSuccess({
              upgradedResearcher: { ...upgradedResearcher, account: role },
            });
          }),
          catchError((error) => of(researcherActions.changeUmResearcherRoleError(error)))
        );
      })
    )
  );

  changeUmResearcherRoleSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.changeUmResearcherRoleSuccess),
        tap(() => this.toastrService.success('Researcher successfully upgraded'))
      ),
    { dispatch: false }
  );

  changeUmResearcherRoleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.changeUmResearcherRoleError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to upgrade Researcher'
          )
        )
      ),
    { dispatch: false }
  );

  changeUmResearcherStudyType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.changeUmResearcherStudyType),
      switchMap(({ researcher, checked, selectedStudyType }) => {
        return this.umResearchersService.changeResearcherStudyType(researcher.id, selectedStudyType, checked).pipe(
          map(() =>
            researcherActions.changeUmResearcherStudyTypeSuccess({
              changedStudyTypeResearcher: researcher,
              checked,
              selectedStudyType,
            })
          ),
          catchError((error) => of(researcherActions.changeUmResearcherStudyTypeError(error)))
        );
      })
    )
  );

  changeUmResearcherStudyTypeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.changeUmResearcherStudyTypeSuccess),
      switchMap(({ changedStudyTypeResearcher, selectedStudyType, checked }) =>
        of(
          researcherActions.changeStudyTypeAccess({
            researcher: changedStudyTypeResearcher,
            selectedStudyType,
            checked,
          })
        )
      )
    )
  );

  changeUmResearcherStudyTypeError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.changeUmResearcherStudyTypeError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to change Researcher allowed study types'
          )
        )
      ),
    { dispatch: false }
  );

  // TODO ovo treba da bude study a ne researcher
  changeStudyTypeAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.changeStudyTypeAccess),
      switchMap(({ researcher, checked, selectedStudyType }) => {
        return this.umResearchersService.changeStudyTypeAccess(researcher.id, selectedStudyType, !checked).pipe(
          map(() =>
            researcherActions.changeStudyTypeAccessSuccess({
              changedStudyTypeAccessResearcher: researcher,
              checked,
            })
          ),
          catchError((error) => of(researcherActions.changeStudyTypeAccessError(error)))
        );
      })
    )
  );

  changeStudyTypeAccessSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.changeStudyTypeAccessSuccess),
      tap(({ checked }) => {
        if (checked) {
          this.toastrService.success('Successfully unblocked all studies of this type');
        } else {
          this.toastrService.success('Successfully blocked all studies of this type');
        }
      }),
      switchMap(({ changedStudyTypeAccessResearcher }) =>
        of(
          umStudyActions.getResearcherStudies({
            researcherId: changedStudyTypeAccessResearcher.id,
          })
        )
      )
    )
  );

  changeStudyTypeAccessError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.changeStudyTypeAccessError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to block/allow type of studies'
          )
        )
      ),
    { dispatch: false }
  );

  disableResearcherTFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(researcherActions.disableUmResearcherTFA),
      switchMap(({ researcher }) => {
        return this.umResearchersService.disableResearcherTFA(researcher.id).pipe(
          map(() =>
            researcherActions.disableUmResearcherTFASuccess({
              disabledResearcherTFA: { ...researcher, tfaSet: 0 },
            })
          ),
          catchError((error) => of(researcherActions.disableUmResearcherTFAError(error)))
        );
      })
    )
  );

  disableResearcherTFASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.disableUmResearcherTFASuccess),
        tap(() => this.toastrService.success('Researcher TFA successfully disabled.'))
      ),
    { dispatch: false }
  );

  disableResearcherTFAError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(researcherActions.disableUmResearcherTFAError),
        tap((error) =>
          this.toastrService.error(
            error.hasOwnProperty('error') ? error.error.toString() : 'Unable to disable Researcher TFA.'
          )
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private toastrService: ToastrService,
    private umResearchersService: ResearcherService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe
  ) {}
}
